import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from '../hooks/useIsMobile';

const Z_INDEX = 9999;

/**
 * On mobile: bottom sheet (common mobile behavior)
 * On desktop: dropdown positioned near trigger
 */
export default function DropdownOrBottomSheet({ open, onClose, triggerRef, children, title, className = '' }) {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!open) return;
    const onEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEscape);
    document.body.style.overflow = open && isMobile ? 'hidden' : '';
    return () => {
      document.removeEventListener('keydown', onEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose, isMobile]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (triggerRef?.current?.contains(e.target)) return;
      if (e.target.closest('[data-dropdown-or-sheet]')) return;
      onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onClose, triggerRef]);

  if (!open) return null;

  if (isMobile) {
    return createPortal(
      <>
        <div
          className="fixed inset-0 bg-black/40 z-[9998]"
          style={{ zIndex: Z_INDEX - 1 }}
          onClick={onClose}
          aria-hidden
        />
        <div
          data-dropdown-or-sheet
          className="fixed left-0 right-0 bottom-0 z-[9999] bg-white dark:bg-gray-800 rounded-t-2xl shadow-xl border-t border-gray-200 dark:border-gray-700 pb-safe"
          style={{ zIndex: Z_INDEX }}
          role="dialog"
          aria-modal="true"
          aria-label={title || 'Opcije'}
        >
          <div className="pt-3 pb-2">
            <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600 mx-auto" />
          </div>
          {title && (
            <p className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
          )}
          <div className={`max-h-[70vh] overflow-y-auto py-2 ${className}`}>
            {children}
          </div>
        </div>
      </>,
      document.body
    );
  }

  // Desktop: dropdown positioned near trigger
  if (!triggerRef?.current) return null;
  const rect = triggerRef.current.getBoundingClientRect();
  return createPortal(
    <div
      data-dropdown-or-sheet
      className="fixed bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-[9999] min-w-[240px] py-1"
      style={{
        top: rect.bottom + 4,
        right: typeof window !== 'undefined' ? window.innerWidth - rect.right : 0,
        zIndex: Z_INDEX,
      }}
      role="menu"
    >
      {children}
    </div>,
    document.body
  );
}
